import type { BlockPerimeters } from "building-types/blocks/blockTypes";
import type { BuildingGraph } from "building-types/building/buildingGraphTypes";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { EntitySettings } from "building-types/entitySettings/entitySettingsTypes";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { EdgeModifier } from "building-types/modifiers/edgeModifierTypes";
import type { GeneratedModifier } from "building-types/modifiers/generatedModifier";
import type { RemoveFormula } from "building-types/options/formula";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { RoofVolumeInputSettings } from "building-types/roofs/roofInputSettings";
import type {
  EvaluatedRoof,
  EvaluatedRoofData,
  RoofSystem,
  RoofSystemInternal,
} from "building-types/roofs/roofSystemTypes";
import { isIndexedRoofPeakDirection, RoofFlags } from "building-types/roofs/roofSystemTypes";
import type { GenerateRoofVolumeOptions } from "building-types/roofVolumes/roofVolumeGenerationTypes";
import type { GeneratedRoofVolume, GeneratedRoofVolumeLegacy } from "building-types/roofVolumes/roofVolumeTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { DormerSettings } from "building-types/settings/dormerSettings";
import type { MergeSettingsForEntityParams } from "building-types/settings/mergeSettingsForEntityParams";
import type { RoofFramingSettings } from "building-types/settings/roofFramingSettings";
import type { RoofSettings } from "building-types/settings/roofSettings";
import { type NeighborRoof, RoofSide, type RoofSystemLevel } from "building-types/settings/roofTypes";
import type { SettingsFragment } from "building-types/settings/settingsFragment";
import type { SettingsSet } from "building-types/settings/settingsSet";
import { EaveFasciaHeightDatum, GableRoofType, RoofPeakDirection } from "building-types/settings/settingsTypesEnum";
import type { WallSettings } from "building-types/settings/wallSettings";
import type { WallPlaneMasses } from "building-types/walls2/wallPlane";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNull } from "common/utils/assertOfType";
import { assertIsNotFormula } from "doc/formula/formulaUtils";
import { neighborRoofNew, neighborRoofsNew } from "doc/utils/roofTools";
import { getExteriorWallOffset } from "doc/utils/wallTypeTools";
import { DIST_EPSILON } from "geom/core";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import {
  polyLine2EdgeCount,
  polyLine2EdgeEval,
  polyLine2EdgeLength,
  polyLine2EdgeTangent,
  polyLine2IsHorizontalEdge,
} from "geom/polyLine2";
import { vec2Dot } from "geom/vec2";
import { getEaveDepth } from "../../../elements/roofTrimUtility";
import { getCombinedMirrorAxes, isCombinedGlobalInstanceMirror } from "../../../generationTools/generateOpeningsTools";
import { multiGableRoofClipContextByUpperFloorBSP } from "../../multiGableRoofClipContext";
import { generateRoofSystemLevel, isVirtualRoofBlockCCW } from "../../multiGableRoofs";
import { getSubRoofBlocks } from "../../multiGableRoofTools";
import { getFinalDormerSettings } from "../../roofs";
import getMergedRoofSettings from "../../roofs/getMergedRoofSettings";
import {
  filterAndGetRoofSystems,
  getRoofLevelId,
  getRoofSystemLevelId,
  getSortedRoofs,
} from "../../roofs/getRoofSystems";
import { fixRoofEdgeOverrides, fixRoofSideOverrides } from "../../roofs/roofEdges";
import {
  invertRoofHeelHeightSideOffsets,
  makeRoofHeelHeightOffsets,
  makeRoofHeelHeightOffsetsLegacy,
  offsetGableRoofHeelHeightOffsets,
} from "../../roofs/roofHeelHeightOffsets";
import { getNeighborRoofs } from "../../roofs/roofNeighbors";
import { nonEmptyGableSubRoof } from "../../roofs/roofSubRoofFilters";
import { filterUpperFloorPerimeters, getBaseHeightFromHostRoof } from "../../roofTools/multiGableRoofTools";
import { hasRoofFlag } from "../../roofTools/roofVolumeFlagsAndEdges";
import { generateRoofVolumeLegacy } from "./generateRoofVolume";

interface MakeRoofSystemVolumesLegacyOptions extends MergeSettingsForEntityParams {
  readonly level: Level;
  readonly wallMasses: Table<WallMassSystem>;
  readonly polygonPlanarGraphs: Table<PolygonPlanarGraph>;
  readonly roofSystemLevels: Table<RoofSystemLevel>;
  readonly clipContext: MultiGableRoofClipContext;
  readonly roofSystemId: string;
  readonly roofSystem: RoofSystemInternal;
  readonly filteredSubRoofs: EvaluatedRoof[];
  readonly roofVolumeInputSettings: RoofVolumeInputSettings;
  readonly blockLevelIds: { [blockId: string]: string };
  readonly levels: Table<Level>;
  readonly issues: BuildingGenerationIssue[];
  readonly mirror: boolean;
  readonly generatedRoofVolumes: RWTable<GeneratedRoofVolume>;
  readonly blockPerimeters: BlockPerimeters;
  readonly wallPlanes?: boolean;
  readonly wallPlaneMasses?: WallPlaneMasses;
  readonly buildingGraph?: BuildingGraph;
}

function makeRoofSystemVolumesLegacy({
  roofSystemId,
  roofSystem,
  level,
  mirror,
  levels,
  issues,
  roofVolumeInputSettings,
  roofSystemLevels,
  filteredSubRoofs,
  entitySettings,
  activeSettingsSets,
  optionChoices,
  settings,
  blockLevelIds,
  wallMasses,
  polygonPlanarGraphs,
  clipContext,
  generatedRoofVolumes,
  blockPerimeters,
  wallPlanes,
  wallPlaneMasses,
  buildingGraph,
}: MakeRoofSystemVolumesLegacyOptions) {
  const { roofSettings, wallSettings, roofFramingSettings, dormerSettings } = roofVolumeInputSettings;

  const multiGableRoofLevel = roofSystemLevels[level.id];

  const {
    roofIdToPolygonIndex: subGableRoofIdToPolygonIndex,
    roofPolygonIndexToRoofId: subGablePolygonIndexToRoofId,
    roofPerimeters: subGableRoofsPerimeters,
    roofPlanarGraphs: subGableRoofsPlanarGraph,
  } = multiGableRoofLevel;

  const { subRoofs } = roofSystem;

  const insertedRoofVolumes: RWTable<GeneratedRoofVolumeLegacy> = {};

  if (!(Object.values(roofSystem.subRoofs).length > 0)) {
    console.assert(false, "Multi gable system must have one or more roofs");
  }

  const blockIdToSubRoofId: RWTable<string> = {};

  const exteriorWallOffsetWithSheathing = getExteriorWallOffset(
    wallSettings,
    WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE,
  );

  // find the valid subroofs
  const validSubRoofsIds: string[] = [];
  for (const { id } of filteredSubRoofs) {
    if (subGableRoofsPerimeters[id] !== undefined) {
      validSubRoofsIds.push(id);
    }
  }

  if (validSubRoofsIds.length === 0) {
    return undefined;
  }

  // iterate through valid subroofs
  let index = 0;
  for (const roofId of validSubRoofsIds) {
    const polygonId = subGableRoofIdToPolygonIndex[roofId];
    const inputPerimeters = subGableRoofsPerimeters[roofId];
    const subRoof = subRoofs[roofId];
    const { boundingPerimeter, exactPerimeter } = inputPerimeters;
    const combinedMirror = isCombinedGlobalInstanceMirror(mirror, subRoof);
    const { combinedFlipX, combinedFlipY } = getCombinedMirrorAxes(mirror, subRoof?.componentTransform);
    if (!(combinedMirror === (combinedFlipX !== combinedFlipY))) {
      console.assert(false, `combinedMirror does not combinedFlipX XOR combinedFlipY for subRoof ${roofId}`);
    }

    if (!boundingPerimeter) {
      continue;
    }

    let finalRoofSettings = getMergedRoofSettings({
      roof: subRoof,
      homeSettings: roofSettings,
      entitySettings,
      activeSettingsSets,
      optionChoices,
      settings,
    });

    let finalRoofFramingSettings = roofFramingSettings;
    const finalDormerSettings = getFinalDormerSettings(subRoof.dormerSettings, dormerSettings);

    const {
      peakDirection,
      eaveFasciaHeightDatum = EaveFasciaHeightDatum.DEFAULT,
      roofType = GableRoofType.DEFAULT,
    } = finalRoofSettings;

    const isShedRoof = roofType !== GableRoofType.DEFAULT;
    const currLevelId = getRoofLevelId(subRoof, blockLevelIds, levels);
    const upperLevelId = isShedRoof ? currLevelId : level.id;
    const { upperFloorPerimeters = {}, upperFloorCeilElevation } = roofSystemLevels[upperLevelId];
    const levelWallMasses = wallMasses[currLevelId];
    const levelPlanarGraph = polygonPlanarGraphs[currLevelId]?.planarGraph;

    const filterForDefaultRoof = (blockId: string): boolean => {
      const { subRoofs } = roofSystem;

      for (const subRoofId in subRoofs) {
        const subRoof = subRoofs[subRoofId];

        const roofBlocks = getSubRoofBlocks(subRoof, blockPerimeters);
        for (const roofBlock of roofBlocks) {
          if (roofBlock === blockId) {
            return false;
          }
        }
      }
      return true;
    };

    const filterForShedRoof = (blockId: string): boolean => {
      const { subRoofs } = roofSystem;

      for (const subRoofId in subRoofs) {
        const subRoof = subRoofs[subRoofId];
        const subRoofLevelId = getRoofLevelId(subRoof, blockLevelIds, levels);

        if (subRoofLevelId !== upperLevelId) {
          continue;
        }

        const roofBlocks = getSubRoofBlocks(subRoof, blockPerimeters);
        for (const roofBlock of roofBlocks) {
          if (roofBlock === blockId) {
            return false;
          }
        }
      }
      return true;
    };

    const filteredUpperFloorPerimeters = filterUpperFloorPerimeters(
      upperFloorPerimeters,
      isShedRoof ? filterForShedRoof : filterForDefaultRoof,
    );

    // calculate the heel height offsets
    const leftSideOverrides = subRoof.roofSideOverrides[RoofSide.LEFT];
    const rightSideOverrides = subRoof.roofSideOverrides[RoofSide.RIGHT];
    const startSideOverrides = subRoof.roofSideOverrides[RoofSide.START];
    const endSideOverrides = subRoof.roofSideOverrides[RoofSide.END];

    const baseOffset = finalRoofSettings.eaveFasciaHeightOffset ?? 0;
    const leftOffset =
      leftSideOverrides?.heelHeightOffset ?? finalRoofSettings?.eaveLeftFasciaHeightOffsetOverride ?? baseOffset;
    const rightOffset =
      rightSideOverrides?.heelHeightOffset ?? finalRoofSettings?.eaveRightFasciaHeightOffsetOverride ?? baseOffset;
    const truncationRefOffset = Math.min(leftOffset, rightOffset);
    const startOffset = startSideOverrides?.normalizedHeelHeightOffset
      ? truncationRefOffset
      : startSideOverrides?.heelHeightOffset ?? baseOffset;
    const endOffset = endSideOverrides?.normalizedHeelHeightOffset
      ? truncationRefOffset
      : endSideOverrides?.heelHeightOffset ?? baseOffset;

    const legacyTruncation = hasRoofFlag(subRoof, RoofFlags.LEGACY_TRUNCATION);
    let heelHeightOffsets = makeRoofHeelHeightOffsets(
      legacyTruncation ? baseOffset : Math.min(baseOffset, 0),
      leftOffset,
      rightOffset,
      legacyTruncation ? baseOffset : startOffset,
      legacyTruncation ? baseOffset : endOffset,
    );

    if (hasRoofFlag(subRoof, RoofFlags.LEGACY_HEEL_HEIGHT_OFFSETS)) {
      heelHeightOffsets = makeRoofHeelHeightOffsetsLegacy(baseOffset, leftOffset, rightOffset);
    }

    heelHeightOffsets = offsetGableRoofHeelHeightOffsets(heelHeightOffsets, getEaveDepth(finalRoofSettings));

    const currLevel = levels[currLevelId];
    const currCeilingHeight = currLevel.floorElevation + currLevel.topOfWall;
    let currBaseHeight = currCeilingHeight + heelHeightOffsets.refOffset;

    const baseEaveOverhang = finalRoofSettings.eaveOverhang;
    assertIsNotFormula(baseEaveOverhang);
    const eaveOverhang = baseEaveOverhang + exteriorWallOffsetWithSheathing;

    // get the neighbor roofs
    let edgeNeighborRoofs = new Array<NeighborRoof>(polyLine2EdgeCount(boundingPerimeter)).fill(
      neighborRoofNew(currBaseHeight),
    );

    edgeNeighborRoofs = getNeighborRoofs({
      roofSystemId,
      polygonId,
      subRoof,
      boundingPerimeter,
      exactPerimeter,
      baseHeight: currBaseHeight,
      eaveOverhang,
      baseEaveOverhang,
      clipContext,
      subGablePolygonIndexToRoofId,
      planarGraph: subGableRoofsPlanarGraph,
      insertedRoofVolumes,
      roofSettings: finalRoofSettings,
      roofSideOverrides: subRoof.roofSideOverrides,
      roofEdgeOverrides: subRoof.roofEdgeOverrides,
      mirror: combinedMirror,
    });

    // determine the start and edge edge of the peak
    let startEdge = -1;
    let endEdge = -1;

    // initially assume the slopes at each end of the gable are vertical
    let startNeighbor = neighborRoofNew(currBaseHeight);
    let endNeighbor = neighborRoofNew(currBaseHeight);

    // if a client has manually specified the roof direction
    if (peakDirection !== undefined) {
      const [dx, dy] = polyLine2EdgeTangent(boundingPerimeter, 0);
      // a canonical rect has a horizontal running first edge
      const canonicalRect = Math.abs(vec2Dot(dx, dy, 0, 1)) < DIST_EPSILON;
      if (peakDirection === RoofPeakDirection.HORIZONTAL) {
        if (canonicalRect) {
          startEdge = 1;
          endEdge = 3;
        } else {
          startEdge = 0;
          endEdge = 2;
        }
      } else if (peakDirection === RoofPeakDirection.VERTICAL) {
        if (canonicalRect) {
          startEdge = 0;
          endEdge = 2;
        } else {
          startEdge = 1;
          endEdge = 3;
        }
      } else if (peakDirection === RoofPeakDirection.BY_LOW_RAIL_INDEX) {
        startEdge = 1;
        endEdge = 3;
      } else if (peakDirection === RoofPeakDirection.BY_GABLE_END_RAIL_INDEX) {
        startEdge = 0;
        endEdge = 2;
      } else {
        console.assert(false, `unknown value for PeakDirection for subRoof`);
      }

      startNeighbor = edgeNeighborRoofs[startEdge];
      endNeighbor = edgeNeighborRoofs[endEdge];
    } else {
      // used to flip the AUTO peak direction for shed roofs
      const indexOffset = roofType === GableRoofType.DEFAULT ? 0 : 1;

      // try to automatically detect peak direction from adjacencies
      let bestNeighborIndex = Number.MAX_SAFE_INTEGER;
      for (let j = 0, lj = edgeNeighborRoofs.length; j < lj; j++) {
        const neighborIndex = (j + indexOffset) % lj;
        const neighbor = edgeNeighborRoofs[neighborIndex];
        const neighborRoof = insertedRoofVolumes[neighbor.roofId];
        if (neighborRoof) {
          if (neighborRoof.index < bestNeighborIndex) {
            startEdge = j;
            endEdge = (j + 2) % lj;
            startNeighbor = neighbor;
            endNeighbor = edgeNeighborRoofs[endEdge];

            bestNeighborIndex = neighborRoof.index;
          }
        }
      }

      // if we didn't find a start edge from a roof system neighbor, use the shortest edge
      // as the start edge

      if (startEdge === -1) {
        // the start edge should be the shortest edge of this block
        let shortestEdgeLength = Number.POSITIVE_INFINITY;

        for (let j = 0, lj = edgeNeighborRoofs.length; j < lj; j++) {
          const neighborIndex = j % lj;
          const len = polyLine2EdgeLength(boundingPerimeter, neighborIndex);
          if (len < shortestEdgeLength) {
            startEdge = j;
            endEdge = (j + 2) % lj;
            shortestEdgeLength = len;
          }
        }
      }
    }

    const prevBaseHeight = currBaseHeight;

    currBaseHeight = getBaseHeightFromHostRoof({
      eaveFasciaHeightDatum,
      hostSubRoof: insertedRoofVolumes[startNeighbor.roofId],
      currBaseHeight,
      heelHeightOffset: heelHeightOffsets.refOffset - getEaveDepth(roofSettings),
    });

    currBaseHeight = getBaseHeightFromHostRoof({
      eaveFasciaHeightDatum,
      hostSubRoof: insertedRoofVolumes[endNeighbor.roofId],
      currBaseHeight,
      heelHeightOffset: heelHeightOffsets.refOffset - getEaveDepth(roofSettings),
    });

    heelHeightOffsets = offsetGableRoofHeelHeightOffsets(heelHeightOffsets, currBaseHeight - prevBaseHeight);

    // keep edge coherence
    const [startX, startY] = polyLine2EdgeEval(boundingPerimeter, startEdge, 0);
    const [endX, endY] = polyLine2EdgeEval(boundingPerimeter, endEdge, 0);

    let flattenLeftSideSlopes = subRoof.flattenLeftSideSlopes;
    let flattenRightSideSlopes = subRoof.flattenRightSideSlopes;
    let flipBaseSlopes = false;
    let flipTruncationOrder = false;

    const flipSideParameters = () => {
      let newRoofType = roofType;
      if (roofType === GableRoofType.LEFT_SIDE_SHED_ROOF) {
        newRoofType = GableRoofType.RIGHT_SIDE_SHED_ROOF;
      } else if (finalRoofSettings.roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF) {
        newRoofType = GableRoofType.LEFT_SIDE_SHED_ROOF;
      }

      const tmpFlatten = flattenLeftSideSlopes;
      flattenLeftSideSlopes = flattenRightSideSlopes;
      flattenRightSideSlopes = tmpFlatten;

      finalRoofSettings = {
        ...finalRoofSettings,
        slope: finalRoofSettings.rightSlope ?? finalRoofSettings.slope,
        rightSlope: finalRoofSettings.slope,
        slopes: finalRoofSettings.rightSlopes ?? finalRoofSettings.slopes,
        rightSlopes: finalRoofSettings.slopes,
        roofType: newRoofType,
      };

      heelHeightOffsets = invertRoofHeelHeightSideOffsets(heelHeightOffsets);
      flipBaseSlopes = true;
    };

    const flipEndParameters = () => {
      finalRoofSettings = {
        ...finalRoofSettings,
        startTruncationHeightFactor: finalRoofSettings.endTruncationHeightFactor,
        startTruncationNormalizedHeight: finalRoofSettings.endTruncationNormalizedHeight,
        startTruncationSlope: finalRoofSettings.endTruncationSlope,
        startTruncationSlopeType: finalRoofSettings.endTruncationSlopeType,
        startTruncationType: finalRoofSettings.endTruncationType,
        endTruncationHeightFactor: finalRoofSettings.startTruncationHeightFactor,
        endTruncationNormalizedHeight: finalRoofSettings.startTruncationNormalizedHeight,
        endTruncationSlope: finalRoofSettings.startTruncationSlope,
        endTruncationSlopeType: finalRoofSettings.startTruncationSlopeType,
        endTruncationType: finalRoofSettings.startTruncationType,
      };

      finalRoofFramingSettings = {
        ...finalRoofFramingSettings,
        startFramingType: finalRoofFramingSettings.endFramingType,
        endFramingType: finalRoofFramingSettings.startFramingType,
      };

      flipTruncationOrder = true;
    };

    const swapEdges = () => {
      const tmpEdge = startEdge;
      startEdge = endEdge;
      endEdge = tmpEdge;
    };

    if (peakDirection !== undefined && isIndexedRoofPeakDirection[peakDirection]) {
      if (isVirtualRoofBlockCCW(subRoof)) {
        swapEdges();
      }

      if (combinedMirror) {
        flipEndParameters();
      }
    } else {
      if (polyLine2IsHorizontalEdge(boundingPerimeter, startEdge)) {
        if (startY > endY !== combinedFlipY) {
          swapEdges();
        }
      } else {
        if (startX > endX !== combinedFlipX) {
          swapEdges();
        }
      }

      if (combinedMirror) {
        flipSideParameters();
      }
    }

    const neighborRoofs = neighborRoofsNew(
      edgeNeighborRoofs[startEdge],
      edgeNeighborRoofs[endEdge],
      isShedRoof ? edgeNeighborRoofs[(startEdge + 3) % edgeNeighborRoofs.length] : neighborRoofNew(currBaseHeight),
      isShedRoof ? edgeNeighborRoofs[(startEdge + 1) % edgeNeighborRoofs.length] : neighborRoofNew(currBaseHeight),
    );

    const subRoofBlocks = getSubRoofBlocks(subRoof, blockPerimeters);

    const finalRoofVolumeInputSettings: RoofVolumeInputSettings = {
      ...roofVolumeInputSettings,
      dormerSettings: finalDormerSettings,
      roofSettings: finalRoofSettings,
      roofFramingSettings: finalRoofFramingSettings,
    };

    const roofData: EvaluatedRoofData = {
      ...subRoof,
      roofSideOverrides: fixRoofSideOverrides(subRoof.roofSideOverrides, combinedMirror, peakDirection),
      roofEdgeOverrides: fixRoofEdgeOverrides(
        subRoof.roofEdgeOverrides,
        polyLine2EdgeCount(inputPerimeters.exactPerimeter),
        finalRoofSettings,
        subRoof.blocks,
        combinedMirror,
      ),
    };

    // generate sub roof
    const subGableOpts: GenerateRoofVolumeOptions = {
      roofData,
      blocks: subRoof.blocks,
      subRoofBlocks,
      roofId,
      index,
      name: subRoof.name,
      level: levels[getRoofLevelId(subRoof, blockLevelIds, levels)],
      levelWallMasses,
      levelPlanarGraph,
      roofInputSettings: finalRoofVolumeInputSettings,
      polygonId,
      inputPerimeters,
      startEdge,
      peakDirection,
      endEdge,
      baseHeight: currBaseHeight,
      ceilingHeight: currCeilingHeight,
      topOfWallHeight: currCeilingHeight,
      neighborRoofs,
      insertedRoofVolumes,
      clipContext,
      issues,
      heelHeightOffsets,
      upperFloorPerimeters: filteredUpperFloorPerimeters,
      upperFloorCeilElevation,
      mirror: combinedMirror,
      flipBaseSlopes,
      flipTruncationOrder,
      wallPlanes: wallPlanes ?? false,
      wallPlaneMasses,
      buildingGraph,
    };

    const { roofVolume, index: newIndex } = generateRoofVolumeLegacy(subGableOpts);
    index = newIndex;

    // insert sub roofs
    for (const subRoofBlock of subRoofBlocks) {
      blockIdToSubRoofId[subRoofBlock] = roofId;
    }

    insertedRoofVolumes[roofId] = roofVolume;
    generatedRoofVolumes[roofId] = roofVolume;
  }
}

interface MakeRoofSystemsVolumesLegacyOptions {
  readonly roofSystems: Table<RoofSystem>;
  readonly roofs: Table<EvaluatedRoof>;
  readonly levels: Table<Level>;
  readonly blockPerimeters: BlockPerimeters;
  readonly blockLevelIds: Table<string>;
  readonly mirror: boolean;
  readonly mirrorTranslationX: number;
  readonly wallSettings: WallSettings;
  readonly roofSettings: RemoveFormula<RoofSettings>;
  readonly dormerSettings: DormerSettings;
  readonly roofFramingSettings: RoofFramingSettings;
  readonly resolvedOptionChoices: OptionChoices;
  readonly edgeModifiers: Table<EdgeModifier>;
  readonly modifiersByInstanceChain: Table<Table<GeneratedModifier>>;
  readonly settings: Table<SettingsFragment>;
  readonly entitySettings: Table<EntitySettings>;
  readonly activeSettingsSets: Table<SettingsSet>;
  readonly modifiers: Table<GeneratedModifier>;
  readonly idToParents: Table<readonly string[]>;
  readonly blockIsHalfStory: Table<boolean>;
  readonly rooms: Table<Room>;
  readonly roomFloorAssemblies: Table<FloorAssembly>;
  readonly roomLevelIndices: Table<number>;
  readonly polygonPlanarGraphs: Table<PolygonPlanarGraph>;
  readonly wallMasses: Table<WallMassSystem>;
  readonly generatedRoofVolumes: RWTable<GeneratedRoofVolume>;
  readonly roofSystemLevels: RWTable<RoofSystemLevel>;
  readonly roofSystemClipContexts: RWTable<MultiGableRoofClipContext>;
  readonly issues: BuildingGenerationIssue[];
  readonly isolatedEntity?: DocEntityHandle;
  readonly wallPlanes?: boolean;
  readonly wallPlaneMasses?: WallPlaneMasses;
  readonly buildingGraph?: BuildingGraph;
}

export function makeRoofSystemsVolumesLegacy({
  roofSystems: unfilteredRoofSystems,
  roofs: unfilteredRoofs,
  levels,
  blockPerimeters,
  blockLevelIds,
  mirror,
  mirrorTranslationX,
  wallSettings,
  roofSettings,
  dormerSettings,
  roofFramingSettings,
  resolvedOptionChoices,
  edgeModifiers,
  modifiersByInstanceChain,
  settings,
  entitySettings,
  activeSettingsSets,
  modifiers,
  idToParents,
  blockIsHalfStory,
  rooms,
  roomFloorAssemblies,
  roomLevelIndices,
  polygonPlanarGraphs,
  wallMasses,
  generatedRoofVolumes,
  roofSystemLevels,
  roofSystemClipContexts,
  issues,
  isolatedEntity,
  wallPlanes,
  wallPlaneMasses,
  buildingGraph,
}: MakeRoofSystemsVolumesLegacyOptions) {
  const expandedRoofSystems = filterAndGetRoofSystems({
    unfilteredRoofSystems,
    unfilteredRoofs,
    levels,
    blockPerimeters,
    blockLevelIds,
    mirror,
    mirrorTranslationX,
    modifiers,
    edgeModifiers,
    modifiersByInstanceChain,
    idToParents,
    isolatedEntity,
  });

  for (const levelId in levels) {
    roofSystemLevels[levelId] = generateRoofSystemLevel({
      levelId: levelId,
      blockLevelIds,
      blockIsHalfStory,
      rooms,
      roomFloorAssemblies,
      roomLevelIndices,
      polygonPlanarGraphs,
      levels,
      blockPerimeters,
      roofSystems: expandedRoofSystems,
      roofSettings,
      wallSettings,
      mirror,
      wallMasses,
      issues,
      entitySettings,
      activeSettingsSets,
      settings,
      optionChoices: resolvedOptionChoices,
      wallPlaneEnabled: wallPlanes,
    });
  }

  for (const levelId in roofSystemLevels) {
    const clipContext = multiGableRoofClipContextByUpperFloorBSP(roofSystemLevels[levelId].upperFloorBSP);
    if (wallPlanes) {
      clipContext.individualInsideBSPs = {};
    }
    roofSystemClipContexts[levelId] = clipContext;
  }

  for (const roofSystemId in expandedRoofSystems) {
    const roofSystem = expandedRoofSystems[roofSystemId];
    const filteredSubRoofs = getSortedRoofs(roofSystem, nonEmptyGableSubRoof);

    if (filteredSubRoofs.length === 0) {
      continue;
    }

    const roofSystemLevelId = getRoofSystemLevelId(roofSystem, blockLevelIds, levels);
    const level = roofSystemLevelId !== null ? levels[roofSystemLevelId] : undefined;

    if (!level) {
      console.error(
        `Cannot generate roof system as level id ${roofSystemLevelId} not found for roof system ${roofSystem.name}. It may have been deleted or disabled.`,
      );
      continue;
    }

    assertIsNotNull(roofSystemLevelId);

    const clipContext = roofSystemClipContexts[roofSystemLevelId];

    const roofVolumeInputSettings: RoofVolumeInputSettings = {
      roofSettings,
      roofFramingSettings,
      wallSettings,
      dormerSettings,
      roofPlanSettings: {},
      roofElevationPlanSettings: {},
    };

    makeRoofSystemVolumesLegacy({
      level,
      wallMasses,
      polygonPlanarGraphs,
      blockPerimeters,
      roofSystemLevels,
      clipContext,
      roofSystemId,
      roofSystem,
      filteredSubRoofs,
      roofVolumeInputSettings,
      blockLevelIds,
      levels,
      issues,
      mirror,
      entitySettings,
      activeSettingsSets,
      settings,
      optionChoices: resolvedOptionChoices,
      generatedRoofVolumes,
      wallPlanes,
      wallPlaneMasses,
      buildingGraph,
    });
  }
}
